import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Calendar, Mail, Activity, Eye, ExternalLink, LogOut, Package,
  MessageCircle, FolderOpen, Megaphone, Pin, Scan, FileCheck,
  Footprints, GripVertical, EyeOff, EyeIcon, Settings, Building2, Calculator, Menu, Mic
} from "lucide-react";
import { toast } from "sonner";
import BottomNav from "./BottomNav";
import { ThyroidIcon } from "@/components/thyroid/ThyroidIcon";
import { AppSidebar } from "@/pages/AppSidebar";

const getToken = () => localStorage.getItem("session_token");
const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ALL_MODULES = [
  { id: "schedule", label: "Töötajate graafik", desc: "Töögraafikud ja vahetused", icon: Calendar, color: "violet", route: "/schedule" },
  { id: "chat", label: "MedChat", desc: "Sisemine vestlus kolleegidega", icon: MessageCircle, color: "teal", route: "/chat" },
  { id: "notices", label: "Teadetetahvel", desc: "Kliiniku teated ja uudised", icon: Megaphone, color: "amber", route: "/notices" },
  { id: "reminders", label: "Patsientide meeldetuletused", desc: "E-posti ja SMS teavitused", icon: Mail, color: "emerald", route: "/reminders" },
  { id: "retinal", label: "Silmapõhja analüüs", desc: "AI-põhine retinopaatia tuvastamine", icon: Eye, color: "sky", route: "/retinal" },
  { id: "ulcers", label: "Diabeetiline jalahaavand", desc: "TIMERS haavahindamine", icon: Footprints, color: "cyan", route: "/ulcers" },
  { id: "consents", label: "Patsiendi nõusolekud", desc: "Nõusolekute haldus", icon: FileCheck, color: "lime", route: "/consents" },
  { id: "thyroid", label: "Kilpnäärme Kliinik", desc: "AI kilpnäärme analüüs", icon: ThyroidIcon, color: "rose", route: "/thyroid" },
  { id: "graves", label: "Gravesi orbitopaatia", desc: "AI-põhine CAS hindamine", icon: Scan, color: "fuchsia", route: "/graves" },
  { id: "supplies", label: "MedTarvikud", desc: "Tarvikute haldus ja tellimine", icon: Package, color: "orange", route: "/supplies" },
  { id: "medivoice", label: "MediVoice", desc: "AI vestluse kokkuvõte", icon: Mic, color: "indigo", route: "/medivoice" },
  { id: "documents", label: "MedDoc", desc: "Ravijuhendid ja protokollid", icon: FolderOpen, color: "cyan", route: "/documents" },
];

const COLOR_MAP = {
  violet: { bg: "bg-violet-100", text: "text-violet-600" },
  teal: { bg: "bg-teal-100", text: "text-teal-600" },
  amber: { bg: "bg-amber-100", text: "text-amber-600" },
  emerald: { bg: "bg-emerald-100", text: "text-emerald-600" },
  sky: { bg: "bg-sky-100", text: "text-sky-600" },
  cyan: { bg: "bg-cyan-100", text: "text-cyan-600" },
  lime: { bg: "bg-lime-100", text: "text-lime-600" },
  rose: { bg: "bg-rose-100", text: "text-rose-600" },
  fuchsia: { bg: "bg-fuchsia-100", text: "text-fuchsia-600" },
  orange: { bg: "bg-orange-100", text: "text-orange-600" },
  indigo: { bg: "bg-indigo-100", text: "text-indigo-600" },
};

export default function MainPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [pinnedNotices, setPinnedNotices] = useState([]);
  const [moduleOrder, setModuleOrder] = useState([]);
  const [hiddenModules, setHiddenModules] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [orgLogo, setOrgLogo] = useState(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreviewedCounts, setUnreviewedCounts] = useState({});
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const userEmail = user?.email || user?.user?.email || "";
  const userOrgId = user?.org_id || user?.user?.org_id || "";
  const isAdmin = userEmail === "verner.fogel@gmail.com" ||
    user?.role === "org_admin" || user?.user?.role === "org_admin" ||
    user?.role === "superadmin" || user?.user?.role === "superadmin";

  // Fetch unread + notices + module prefs
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await fetch(`${API}/chat/unread/total`, { headers: authHeaders() });
        if (res.ok) setUnreadTotal((await res.json()).total || 0);
      } catch { /* ok */ }
    };
    const fetchNotices = async () => {
      try {
        const res = await fetch(`${API}/notices`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setPinnedNotices(data.filter(n => n.pinned).slice(0, 3));
        }
      } catch { /* ok */ }
    };
    const fetchPrefs = async () => {
      if (!userOrgId) { setPrefsLoaded(true); return; }
      try {
        const res = await fetch(`${API}/admin/organizations/${userOrgId}/module-prefs`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          if (data.module_order?.length) setModuleOrder(data.module_order);
          if (data.hidden_modules?.length) setHiddenModules(data.hidden_modules);
        }
      } catch { /* ok */ }
      setPrefsLoaded(true);
    };
    const fetchLogo = async () => {
      if (!userOrgId) return;
      try {
        const res = await fetch(`${API}/admin/organizations/${userOrgId}/logo`, { headers: authHeaders() });
        if (res.ok) {
          const blob = await res.blob();
          setOrgLogo(URL.createObjectURL(blob));
        }
      } catch { /* ok */ }
    };

    fetchUnread();
    fetchNotices();
    fetchPrefs();
    fetchLogo();
    // Fetch unreviewed counts for doctor review badges
    const fetchUnreviewed = async () => {
      const endpoints = [
        { key: "retinal", url: `${API}/retinal/unreviewed-count` },
        { key: "ulcers", url: `${API}/ulcers/unreviewed-count` },
        { key: "graves", url: `${API}/graves/unreviewed-count` },
        { key: "thyroid", url: `${API}/thyroid/pending-count` },
      ];
      const counts = {};
      await Promise.all(endpoints.map(async ({ key, url }) => {
        try {
          const res = await fetch(url, { headers: authHeaders() });
          if (res.ok) { const d = await res.json(); counts[key] = d.count || 0; }
        } catch { /* ignore */ }
      }));
      setUnreviewedCounts(counts);
    };
    fetchUnreviewed();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [userOrgId]);

  // Build ordered modules list
  const getOrderedModules = useCallback(() => {
    const order = moduleOrder.length ? moduleOrder : ALL_MODULES.map(m => m.id);
    const moduleMap = {};
    ALL_MODULES.forEach(m => { moduleMap[m.id] = m; });
    const ordered = order.filter(id => moduleMap[id]).map(id => moduleMap[id]);
    // Add any new modules not in saved order
    ALL_MODULES.forEach(m => {
      if (!order.includes(m.id)) ordered.push(m);
    });
    return ordered;
  }, [moduleOrder]);

  const visibleModules = getOrderedModules().filter(m => !hiddenModules.includes(m.id));
  const allOrdered = getOrderedModules();

  // Save preferences to backend
  const savePrefs = async (newOrder, newHidden) => {
    if (!userOrgId) return;
    try {
      await fetch(`${API}/admin/organizations/${userOrgId}/module-prefs`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ module_order: newOrder, hidden_modules: newHidden }),
      });
    } catch { /* ok */ }
  };

  // Drag handlers
  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragEnter = (idx) => { dragOverItem.current = idx; };
  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const items = [...allOrdered.map(m => m.id)];
    const [dragged] = items.splice(dragItem.current, 1);
    items.splice(dragOverItem.current, 0, dragged);
    setModuleOrder(items);
    savePrefs(items, hiddenModules);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const toggleHide = (moduleId) => {
    const newHidden = hiddenModules.includes(moduleId)
      ? hiddenModules.filter(id => id !== moduleId)
      : [...hiddenModules, moduleId];
    setHiddenModules(newHidden);
    savePrefs(moduleOrder.length ? moduleOrder : ALL_MODULES.map(m => m.id), newHidden);
    toast.success(hiddenModules.includes(moduleId) ? "Moodul nähtav" : "Moodul peidetud");
  };

  const handleModuleClick = (mod) => {
    if (editMode) return;
    if (mod.external) window.open(mod.external, "_blank");
    else if (mod.route) navigate(mod.route);
  };

  if (!prefsLoaded) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-8 h-8 spinner" /></div>;
  }

  const displayModules = editMode ? allOrdered : visibleModules;

  return (
    <div className="min-h-screen bg-slate-50 pb-16 md:pb-0" data-testid="main-page">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} moduleOrder={moduleOrder} hiddenModules={hiddenModules} mobileOnly />
      <header className="bg-white border-b border-slate-200 px-4 py-4 md:py-6">
        <div className="max-w-4xl lg:max-w-6xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            {/* Mobile hamburger */}
            <button className="md:hidden p-1.5 hover:bg-slate-100 rounded-lg shrink-0" onClick={() => setSidebarOpen(true)} data-testid="main-menu-btn">
              <Menu className="w-5 h-5 text-slate-600" />
            </button>
            {orgLogo ? (
              <img src={orgLogo} alt="Logo" className="w-10 h-10 md:w-12 md:h-12 rounded-xl object-contain shrink-0" />
            ) : (
              <div className="w-10 h-10 md:w-12 md:h-12 bg-slate-900 rounded-xl flex items-center justify-center shrink-0">
                <Activity className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-lg md:text-2xl font-bold text-slate-900 truncate">MedPortaal</h1>
              <p className="text-xs md:text-sm text-slate-500 truncate">Tere, {user?.name || user?.user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 md:gap-2 shrink-0">
            {isAdmin && (
              <Button
                variant={editMode ? "default" : "ghost"}
                size="sm"
                onClick={() => setEditMode(!editMode)}
                className={editMode ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
                data-testid="edit-modules-btn"
              >
                <Settings className="w-4 h-4 md:mr-1" />
                <span className="hidden md:inline">{editMode ? "Valmis" : "Kohanda"}</span>
              </Button>
            )}
            {/* Kalkulaatorid & Keskuste haldus — hidden on mobile, visible in sidebar */}
            <Button variant="ghost" size="sm" onClick={() => navigate("/calculators")} className="hidden md:flex" data-testid="main-calculators-btn">
              <Calculator className="w-4 h-4 mr-1" /> Kalkulaatorid
            </Button>
            {isAdmin && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} className="hidden md:flex" data-testid="main-admin-btn">
                <Building2 className="w-4 h-4 mr-1" /> Keskuste haldus
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={logout} className="hidden md:flex" data-testid="main-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Logi välja
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl lg:max-w-6xl mx-auto p-4 md:p-8">
        <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-4 md:mb-6">
          {editMode ? "Kohanda mooduleid — lohista järjekorda, peida mittevajalikud" : "Vali rakendus"}
        </h2>

        {pinnedNotices.length > 0 && !editMode && (
          <Card className="border-slate-200 border-l-4 border-l-amber-400 mb-4 md:mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Pin className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-semibold text-slate-700">Kinnitatud teated</span>
              </div>
              <div className="space-y-2">
                {pinnedNotices.map(n => (
                  <div key={n.notice_id} className="text-sm cursor-pointer hover:bg-slate-50 rounded p-1.5 -mx-1.5" onClick={() => navigate("/notices")}>
                    <span className="font-medium text-slate-800">{n.title}</span>
                    {n.text && <span className="text-slate-500 ml-1">— {n.text.slice(0, 60)}{n.text.length > 60 ? "..." : ""}</span>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          {displayModules.map((mod, idx) => {
            const Icon = mod.icon;
            const c = COLOR_MAP[mod.color] || COLOR_MAP.violet;
            const isHidden = hiddenModules.includes(mod.id);

            return (
              <div
                key={mod.id}
                draggable={editMode}
                onDragStart={() => handleDragStart(idx)}
                onDragEnter={() => handleDragEnter(idx)}
                onDragEnd={handleDragEnd}
                onDragOver={e => e.preventDefault()}
                className={`h-full ${editMode ? "cursor-grab active:cursor-grabbing" : ""}`}
              >
                <Card
                  className={`border-slate-200 transition-all duration-200 select-none relative h-full ${
                    editMode
                      ? `ring-2 ring-dashed ${isHidden ? "ring-red-300 opacity-50" : "ring-blue-300"}`
                      : "cursor-pointer hover:shadow-lg hover:-translate-y-1"
                  }`}
                  onClick={() => handleModuleClick(mod)}
                  data-testid={`${mod.id}-card`}
                >
                  <CardContent className="p-4 flex items-center gap-4 md:flex-col md:items-center md:text-center md:py-8 md:px-6 lg:py-10 lg:px-8 h-full">
                    {editMode && (
                      <GripVertical className="w-4 h-4 text-slate-300 absolute top-2 left-2" />
                    )}
                    {mod.id === "thyroid" ? (
                      <div className={`w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 ${c.bg} rounded-xl flex items-center justify-center flex-shrink-0 relative md:mb-1 lg:mb-2`}>
                        <Icon className="w-8 h-8 md:w-11 md:h-11 lg:w-14 lg:h-14" />
                        {(unreviewedCounts[mod.id] || 0) > 0 && !editMode && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1" data-testid={`${mod.id}-unreviewed-badge`}>
                            {unreviewedCounts[mod.id]}
                          </span>
                        )}
                      </div>
                    ) : (
                    <div className={`w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 ${c.bg} rounded-xl flex items-center justify-center flex-shrink-0 relative md:mb-1 lg:mb-2`}>
                      <Icon className={`w-6 h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 ${c.text}`} />
                      {mod.id === "chat" && unreadTotal > 0 && !editMode && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1" data-testid="chat-unread-badge">
                          {unreadTotal}
                        </span>
                      )}
                      {(mod.id === "retinal" || mod.id === "ulcers" || mod.id === "graves") && (unreviewedCounts[mod.id] || 0) > 0 && !editMode && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1" data-testid={`${mod.id}-unreviewed-badge`}>
                          {unreviewedCounts[mod.id]}
                        </span>
                      )}
                    </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 md:text-lg">{mod.label}</p>
                      <p className="text-sm text-slate-500">{mod.desc}</p>
                    </div>
                    {editMode && (
                      <button
                        onClick={e => { e.stopPropagation(); toggleHide(mod.id); }}
                        className={`p-1.5 rounded-lg transition-colors ${isHidden ? "bg-red-100 text-red-500 hover:bg-red-200" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}
                        title={isHidden ? "Näita" : "Peida"}
                        data-testid={`hide-${mod.id}`}
                      >
                        {isHidden ? <EyeOff className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                      </button>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </main>

      <BottomNav />
    </div>
  );
}
